import { createElement as rc, useEffect, createContext } from 'react';
import { Route, Switch } from 'react-router';

import { View, fromTheme, styled, webOnlyStyles, hooks, nativeOnlyStyles } from 'lib_ui-primitives';
import { constants } from 'lib_ui-services';
import OfflinePin from './OfflinePin';
import ContractExpired from './ContractExpired';
import VerifyAccountPage from './VerifyAccountPage';
import Logo, { variants } from '../Logo';
import { setupRulesEngine } from 'lib_ui-core';
import useEventSink from '../../hooks/useEventSink';
import LoadingBoundary from '../contextProviders/LoadingBoundary';
import ReadProvider from '../contextProviders/ReadProvider';
import ActiveRecord from '../contextProviders/ActiveRecord';
import RouteReadOwnershipProvider from '../contextProviders/RouteReadOwnershipProvider';

const { useRouter } = hooks;

// Do not center horizontally on mobile or the keyboard will cut off the bottom.
let Centered = styled(View).attrs({ name: 'centered' })`
    min-height: ${fromTheme('height')};
    width: 100%;
    height: 100%;
    flex: 1;
    flex-direction: column;
    align-items: center;
    justify-content: ${({ theme: { mobile } }) => (mobile ? 'space-between' : 'center')};
`;

Centered = webOnlyStyles(Centered)`
    box-sizing: border-box;
`;

// Do not constrain the width on mobile view or the login title will have spaces
// at the edge.  Do not use 'unset' as this will break react native.
let Main = styled(View).attrs({ name: 'main' })`
    flex-direction: column;
    justify-content: center;
    flex-grow: 0;
    max-width: ${({ theme: { mobile } }) => (mobile ? '9999px' : '370px')};
`;
// Prevents the credential area from getting squished on native.
Main = nativeOnlyStyles(Main)`
    flex-shrink: 0;
`;
Main.displayName = 'Main';

const StickyFooter = styled(View).attrs({ name: 'stickyFooter' })`
    justify-content: center;
    padding: 12px 0px;
    flex-grow: 0;
    flex-shrink: 1;
`;

/**
 * Used to capture which component in the route currently owns a sensor using a ReadContext
 */
export const RouteReadContext = createContext({
    sensorOwners: {
        RFID: undefined,
        MANUAL: undefined,
        BARCODE: undefined,
        BLE: undefined
    }
});
const ALL_SENSOR_TYPES = [
    constants.sensorTypes.RFID,
    constants.sensorTypes.MANUAL,
    constants.sensorTypes.BARCODE,
    constants.sensorTypes.BLE
];

export default function Unauthenticated() {
    const eventSink = useEventSink();
    useEffect(() => {
        setupRulesEngine({}, eventSink);
    }, [eventSink]);

    const { query, location, replaceCurrentLocation } = useRouter();
    const path = location.pathname;

    useEffect(() => {
        if (query.token && path !== '/confirm') {
            replaceCurrentLocation(`/confirm?token=${query.token}`);
        }
    }, [replaceCurrentLocation, path, query.token]);

    // prettier-ignore
    return rc(Centered, null,
        rc(LoadingBoundary, null,
            rc(Main, null,
                rc(RouteReadOwnershipProvider, { currentRoute: path, readOwnerId: `${path}` },
                    rc(ReadProvider, { id: 'topReadContext', desiredSensorTypes: ALL_SENSOR_TYPES },
                        rc(Switch, {},
                            // Note: isNew = true on ActiveRecord is to force a CREATE/POST instead of UPDATE/PATCH
                            rc(Route, { path: forAllGroups('/offlinePin') }, rc(ActiveRecord, { namespace: 'security', relation: 'profile', type: 'offlinePin', isNew: true }, rc(OfflinePin, {}))),
                            rc(Route, { path: forAllGroups('/contractExpired') }, rc(ContractExpired)),
                            rc(Route, { path: forAllGroups('/confirm') },
                                rc(ActiveRecord, { namespace: 'security', relation: 'profile', type: 'setPassword', record: { tokenString: query.token, userName: query.userName }, isNew: true },
                                rc(VerifyAccountPage, { token: query.token, userName: query.userName }))
                            )
                        )
                    )
                )
            )
        ),
        rc(StickyFooter, null,
            rc(Logo, { variant: variants.COLOR, addSlogan:true })

        )
    );
}

//Hardcoded groups! If we use more than 2 canary groups, this will break (or better yet, needs to be updated)
//However, the likeliness that we will is small, and doesn't outweigh the complexity that would be required to make it dynamic, somehow.
const forAllGroups = path => ['', '/g/0', '/g/1', '/g/2'].map(g => `${g}${path}`);
